import { SubsidiaryModeAlert } from "PFApp/components/subsidiary_mode_alert";
import { CustomValueState } from "PFTypes";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useBulkActions } from "../hooks/use_bulk_actions";
import css from "./bulk_actions.module.scss";

export const BulkActions = () => {
  const { t } = useTranslation("skillsManagement", { keyPrefix: "bulkActions" });
  const {
    selectedSkills,
    selectNone,
    blacklistSelected,
    approveSelected,
    exportSelected,
    resetSelected,
    mergeSelected
  } = useBulkActions();

  const isAnyGlobalSkillSelected = useMemo(
    () => selectedSkills.some(({ global_id: globalId }) => globalId !== null),
    [selectedSkills]
  );

  const areSelectedOnly = useCallback(
    (...states: CustomValueState[]) => !selectedSkills.some((skill) => !states.includes(skill.state)),
    [selectedSkills]
  );

  const areOnlyMergedOrApprovedSkillsSelected = areSelectedOnly(
    CustomValueState.Approved,
    CustomValueState.Merged
  );

  const areOnlyMergedOrBlockedSkillsSelected = areSelectedOnly(
    CustomValueState.Blocked,
    CustomValueState.Merged
  );

  const areOnlyBlockedSkillsSelected = areSelectedOnly(CustomValueState.Blocked);

  if (selectedSkills.length === 0) {
    return null;
  }

  return (
    <SubsidiaryModeAlert
      className={css.alert}
      onClose={selectNone}
      title={t("selectedCounter", {
        count: selectedSkills.length
      })}
      actions={[
        {
          icon: "check",
          onClick: approveSelected,
          title: areOnlyMergedOrApprovedSkillsSelected
            ? t("titles.cannotApprove", { count: selectedSkills.length })
            : t("titles.approve"),
          disabled: areOnlyMergedOrApprovedSkillsSelected
        },
        {
          icon: "forbidden",
          onClick: blacklistSelected,
          title: areOnlyMergedOrBlockedSkillsSelected
            ? t("titles.cannotBlock", { count: selectedSkills.length })
            : t("titles.block"),
          disabled: areOnlyBlockedSkillsSelected
        },
        {
          icon: "merge",
          onClick: mergeSelected,
          title: isAnyGlobalSkillSelected
            ? t("titles.cannotMergeGlobalSkills")
            : areOnlyMergedOrBlockedSkillsSelected
            ? t("titles.cannotMerge", { count: selectedSkills.length })
            : t("titles.merge"),
          disabled: areOnlyMergedOrBlockedSkillsSelected || isAnyGlobalSkillSelected
        },
        {
          icon: "export",
          onClick: exportSelected,
          title: t("titles.export")
        },
        {
          icon: "history",
          onClick: resetSelected,
          title: t("titles.reset"),
          disabled: isAnyGlobalSkillSelected && !areOnlyBlockedSkillsSelected
        }
      ]}
    />
  );
};
